// Reports → Output, the pure part (OUT-2): who sees what, the range query, the period words, and the series → chart
// buckets. No imports, so tests/outputModel.test.mjs runs it in node as it is.
// OUT-4 (docs/OUTPUT-C5-v1.md §7): three minutes metrics, Scripted · Filmed · Edited, each credited to one role.

const EVERYONE = ['Admin', 'Power User', 'Manager', 'Producer', 'Post Lead'];
export function outputScope(user) {
  const roles = user?.roles || [];
  if (roles.some(r => EVERYONE.includes(r))) return 'everyone';
  if (roles.some(r => r === 'Writer' || r === 'Editor')) return 'own';
  return 'team';
}

// [metric key, box label, who earns it (plural), the role that puts a person on its board]
export const METRICS = [
  ['scripted_min', 'Scripted', 'writers', 'Writer'],
  ['filmed_min', 'Filmed', 'producers', 'Producer'],
  ['edited_min', 'Edited', 'editors', 'Editor'],
];
export const METRIC_KEYS = METRICS.map(m => m[0]);
export const metricLabel = key => (METRICS.find(m => m[0] === key) || [])[1] || key;

// The face of one meter: the named people exactly as the API returns them (scope is the API's, C5 §5; ruling J), less
// the Unassigned row (teamMemberId null), which the meter does not compare (DESIGN rev 2.3 §7) and Details reports.
export const namedPeople = m => (m?.people || []).filter(p => p.teamMemberId);

// Details of one meter (C5 §7.2), as [label, value] pairs. m: the metric's summary; scope: outputScope(); nameOf: how a
// person's name is shown. Credits and pull-backs are the API's per-person counts (OUT-3.3, ruling Q); before those, the
// rollup's one events count was shown as a single line.
export function detailRows(key, m, scope, nameOf = n => n) {
  if (!m) return [];
  const rows = [];
  const named = namedPeople(m);
  const events = named.reduce((s, p) => s + (p.events || 0), 0);
  const un = m.team?.unassigned || { amount: 0, events: 0, pending: 0 };
  const min = n => `${(Math.round((n || 0) * 10) / 10).toFixed(1)} min`;
  const count = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
  if (scope !== 'team') {
    // OUT-3.3 (ruling Q): credits and pull-backs as two lines from the API's counts; an API without them gets the one line
    const own = scope === 'own';
    if (named.every(p => p.pullbacks != null)) {
      rows.push([own ? 'Your credits' : 'Credits', String(named.reduce((s, p) => s + (p.credits || 0), 0))]);
      // Ruling U: a pull-back is dated to its credit's day, so this counts the range's credits taken back since.
      rows.push([own ? 'Your credits later pulled back' : 'Credits later pulled back', String(named.reduce((s, p) => s + p.pullbacks, 0))]);
    } else rows.push([own ? 'Your credits and pull-backs' : 'Credits and pull-backs', String(events)]);
  }
  if (m.pending > 0) rows.push([key === 'edited_min' ? 'No footage to measure' : 'No script to measure', count(m.pending, key === 'scripted_min' ? 'script' : 'card')]);
  if (un.events > 0 || un.amount) rows.push(['Unassigned', `${min(un.amount)} · ${count(un.events, 'event')}`]);
  for (const p of named) if (p.offDays > 0) rows.push([`${nameOf(p.name)}, time off`, `${Math.round(p.offDays * 10) / 10} ${p.offDays === 1 ? 'day' : 'days'}`]);
  return rows;
}

export const RANGES = [['day', 'Day'], ['week', 'Week'], ['month', 'Month'], ['year', 'Year'], ['custom', 'Custom']];
export const RANGE_KEY = 'hf-output:range';   // { [user id]: range }: the range choice per user, nothing else

// ── Days ─────────────────────────────────────────────────────────────────────
const D = ymd => new Date(`${ymd}T00:00:00Z`);
const fmt = (ymd, o) => D(ymd).toLocaleDateString('en-US', { timeZone: 'UTC', ...o });
const addDays = (ymd, n) => { const d = D(ymd); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const mondayOf = ymd => addDays(ymd, -((D(ymd).getUTCDay() + 6) % 7));

export function periodLabel(rg) {
  if (!rg) return '';
  const { range, from, to } = rg;
  if (range === 'day') return fmt(from, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
  if (range === 'week') return `Week of ${fmt(from, { month: 'short', day: 'numeric' })} to ${fmt(to, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  if (range === 'month') return fmt(from, { month: 'long', year: 'numeric' });
  if (range === 'year') return from.slice(0, 4);
  return `${fmt(from, { month: 'short', day: 'numeric', year: 'numeric' })} to ${fmt(to, { month: 'short', day: 'numeric', year: 'numeric' })}`;
}
// The words after a meter's target: "today", "this week" while the range holds today; "that week" for a past one.
export function periodWord(rg) {
  if (!rg) return '';
  const now = rg.from <= rg.today && rg.today <= rg.to;
  const noun = { day: 'day', week: 'week', month: 'month', year: 'year', custom: 'range' }[rg.range];
  if (rg.range === 'day') return now ? 'today' : 'that day';
  // A current period's target is the whole period's (OUT-2.1: the API counts the working days ahead too).
  return `${now ? 'this' : 'that'} ${noun}`;
}

// Series points → chart points. Up to 62 days: one per day; longer: one per week (Monday start), so a year stays readable.
export function bucket(points, rg) {
  if (!points) return { points: [], upTo: -1, weekly: false };
  const weekly = points.length > 62;
  const out = [];
  for (const p of points) {
    const key = weekly ? mondayOf(p.day) : p.day;
    let b = out[out.length - 1];
    if (!b || b.key !== key) {
      const label = weekly ? fmt(key, { month: 'short', day: 'numeric' })
        : points.length <= 7 ? fmt(p.day, { weekday: 'short' }) : fmt(p.day, { month: 'short', day: 'numeric' });
      b = { key, label, short: fmt(p.day, { weekday: 'narrow' }), amount: 0, target: null, from: p.day };
      out.push(b);
    }
    b.amount = Math.round((b.amount + (p.amount || 0)) * 10) / 10;
    if (p.target != null) b.target = Math.round(((b.target || 0) + p.target) * 10) / 10;
  }
  // A month: day numbers on the axis; the full date stays in the tooltip and the table.
  if (!weekly && points.length > 7) for (const b of out) b.axis = String(Number(b.key.slice(8, 10)));
  let upTo = -1;
  out.forEach((b, i) => { if (b.from <= rg.today) upTo = i; });
  return { points: out, upTo, weekly };
}

// The range query: custom sends from and to; the others send the page's "to" date as the anchor (or nothing: today).
export function rangeQuery(range, from, to) {
  if (range === 'custom') return from && to ? `range=custom&from=${from}&to=${to}` : null;
  return `range=${range}${to ? `&to=${to}` : ''}`;
}

// HD-11.1 (P1): at most SERIES_IN_FLIGHT /series calls at a time (useOutput in Output.jsx). seriesQueue(max) returns a
// function that takes a call (() => Promise) and runs it when a slot is free; its promise settles with the call's.
export const SERIES_IN_FLIGHT = 4;
export function seriesQueue(max) {
  let active = 0; const waiting = [];
  const settle = () => { active--; next(); };
  function next() { while (active < max && waiting.length) { active++; const [run, done] = waiting.shift(); Promise.resolve().then(run).then(done.ok, done.err).finally(settle); } }
  return run => new Promise((ok, err) => { waiting.push([run, { ok, err }]); next(); });
}
